// src/components/TopNav.js
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
 *  아이콘 (Heroicons / Lucide 톤의 깔끔한 라인 스타일)
 * ───────────────────────────────────────────── */

const Coffee = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {/* 김 */}
    <path d="M8 4c-.6.8-.6 1.6 0 2.4" />
    <path d="M11.5 4c-.6.8-.6 1.6 0 2.4" />
    <path d="M15 4c-.6.8-.6 1.6 0 2.4" />
    {/* 컵 본체 */}
    <path d="M4 9.5h12v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-6z" />
    {/* 손잡이 */}
    <path d="M16 11.5h2a2.5 2.5 0 0 1 0 5h-2" />
    {/* 받침 */}
    <path d="M3 20h14" />
  </svg>
);

const Candle = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {/* 불꽃 (채움) */}
    <path
      d="M12 3.5c1.6 1.6 2.5 3.1 2.5 4.5a2.5 2.5 0 1 1-5 0c0-1.4.9-2.9 2.5-4.5z"
      fill="currentColor" fillOpacity="0.85" stroke="none"
    />
    {/* 양초 본체 */}
    <rect x="8.5" y="10.5" width="7" height="9.5" rx="1" />
    {/* 받침 */}
    <path d="M7 20h10" />
  </svg>
);

const Flower = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {/* 꽃잎 4개 (튤립/장미 느낌) */}
    <path
      d="M12 4c-2 1.5-3 3-3 5a3 3 0 0 0 6 0c0-2-1-3.5-3-5z"
      fill="currentColor" fillOpacity="0.18"
    />
    <path d="M12 9c-2-.5-3.5 0-4.5 1.8.5 2.4 2.5 3.2 4.5 2.7" />
    <path d="M12 9c2-.5 3.5 0 4.5 1.8-.5 2.4-2.5 3.2-4.5 2.7" />
    {/* 줄기 */}
    <path d="M12 13.5v6.5" />
    {/* 잎 */}
    <path d="M12 17c1.5-.3 3-1.5 3.5-3" />
  </svg>
);

const GradCap = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {/* 학사모 보드 */}
    <path
      d="M2.5 9 L12 5 L21.5 9 L12 13 z"
      fill="currentColor" fillOpacity="0.15"
    />
    {/* 머리 부분(얼굴 아래) */}
    <path d="M5.5 11v3.2c0 1.1 2.9 2.6 6.5 2.6s6.5-1.5 6.5-2.6V11" />
    {/* 술 */}
    <path d="M21.5 9v3.2" />
    <circle cx="21.5" cy="13.5" r="0.9" fill="currentColor" />
  </svg>
);

const Chevron = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/* ─────────────────────────────────────────────
 *  드롭다운 정의
 * ───────────────────────────────────────────── */
const DROPDOWNS = {
  cafe: {
    label: "커피숍",
    Icon: Coffee,
    items: [
      { label: "커피숍소개", to: "/cafe" },
      { label: "음료및디저트", to: "/cafe/menu" },
      { label: "소식", to: "/cafe/scenes" },
    ],
    activePrefixes: ["/cafe"],
  },
  candle: {
    label: "캔들",
    Icon: Candle,
    items: [
      { label: "체험소개", to: "/kerzenziehen/intro" },
      { label: "갤러리", to: "/kerzenziehen/gallery" },
      { label: "체험 스토리", to: "/kerzenziehen/scenes" },
      { label: "예약하기", to: "/reservation/candleselfreservation/" },
    ],
    activePrefixes: ["/kerzenziehen", "/reservation/candleselfreservation"],
  },
  frame: {
    label: "프리저브드",
    Icon: Flower,
    items: [
      { label: "체험소개", to: "/freserved/intro" },
      { label: "갤러리", to: "/freserved/gallery" },
      { label: "체험 스토리", to: "/freserved/scenes" },
      { label: "예약하기", to: "/reservation/frameselfreservation/" },
    ],
    activePrefixes: ["/freserved", "/reservation/frameselfreservation"],
  },
};

/* ─────────────────────────────────────────────
 *  버튼 (아이콘 위, 라벨 아래 — 세로 정렬)
 * ───────────────────────────────────────────── */
function NavBtn({ Icon, label, active, hasChevron, onClick, ariaProps = {} }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "relative flex flex-col items-center justify-center",
        "px-2 md:px-3 h-12 md:h-14 rounded-xl",
        "transition select-none",
        active
          ? "text-white bg-white/10"
          : "text-white/85 hover:text-white hover:bg-white/5",
      ].join(" ")}
      aria-label={label}
      {...ariaProps}
    >
      <span className="relative flex items-center">
        <Icon className="size-5 md:size-6" />
        {hasChevron && (
          <Chevron className="ml-0.5 size-3 opacity-70" />
        )}
      </span>
      <span className="mt-0.5 text-[10px] md:text-[11px] font-bold tracking-wide leading-none">
        {label}
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────
 *  TopNav
 * ───────────────────────────────────────────── */
function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null); // "candle" | "frame" | null
  const wrapRef = useRef(null);

  useEffect(() => { setOpenMenu(null); }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!openMenu) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [openMenu]);

  const isActive = (prefix) => location.pathname.startsWith(prefix);
  const isAnyActive = (prefixes) =>
    Array.isArray(prefixes) && prefixes.some((p) => isActive(p));

  const renderDropdown = (key) => {
    const cfg = DROPDOWNS[key];
    const isOpen = openMenu === key;
    const active = isAnyActive(cfg.activePrefixes);

    return (
      <div className="relative">
        <NavBtn
          Icon={cfg.Icon}
          label={cfg.label}
          active={active || isOpen}
          hasChevron
          onClick={() => setOpenMenu(isOpen ? null : key)}
          ariaProps={{ "aria-haspopup": "menu", "aria-expanded": isOpen }}
        />
        {isOpen && (
          <div
            className="
              absolute left-1/2 -translate-x-1/2 mt-2
              min-w-[180px]
              rounded-2xl overflow-hidden
              bg-[#0b0b10]/95 backdrop-blur-md
              border border-[#d9b36a]/30
              shadow-[0_18px_60px_rgba(0,0,0,0.55)]
            "
            role="menu"
          >
            {cfg.items.map((it, i) => (
              <React.Fragment key={it.to}>
                {i > 0 && <div className="h-px bg-white/10" />}
                <button
                  type="button"
                  onClick={() => { navigate(it.to); setOpenMenu(null); }}
                  className="w-full px-4 py-3 text-left text-sm text-white/90 hover:bg-white/10 transition"
                  role="menuitem"
                >
                  {it.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-[9999]">
      <div className="w-full px-0 md:px-16">
        <div className="mx-auto w-full max-w-[1200px]">
          <div
            className="
              relative w-full
              h-[var(--top-nav-h)]
              select-none
              shadow-[0_10px_35px_rgba(0,0,0,0.25)]
              overflow-visible
              bg-[#0b0b10]/90 backdrop-blur-md
              border-b border-[#d9b36a]/25
            "
          >
            <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#d9b36a] to-transparent opacity-70" />
            <div className="pointer-events-none absolute inset-0">
              <div className="h-full w-full bg-[radial-gradient(ellipse_at_center,rgba(255,215,150,0.10),transparent_60%)]" />
            </div>

            <div className="relative z-10 h-full flex items-center justify-between px-3 md:px-6">
              {/* 로고 (2/3 크기) */}
              <Link
                to="/"
                className="flex items-center flex-shrink-0 min-w-max"
                aria-label="홈으로"
              >
                <img
                  src="/navbarimg/navarlog.png"
                  alt="반딧불 카페"
                  className="h-6 md:h-8 w-auto max-w-none drop-shadow-[0_6px_14px_rgba(0,0,0,0.35)]"
                  draggable={false}
                />
              </Link>

              {/* 4개 메뉴: 아이콘 + 라벨(세로) */}
              <div ref={wrapRef} className="flex items-center gap-0.5 md:gap-2">
                {renderDropdown("cafe")}
                {renderDropdown("candle")}
                {renderDropdown("frame")}
                <NavBtn
                  Icon={GradCap}
                  label="클래스"
                  active={isActive("/eveningclass")}
                  onClick={() => navigate("/eveningclass/about")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default TopNav;
